import { QueryInItInfoResponse } from "@/types/queryInitInfo"
import { RouteRecordRaw } from "vue-router"

const modules = import.meta.glob([
  "../views/desktop/**/*.vue",
  "../components/desktop/**/*.vue",
  "../layouts/desktop/**/*.vue"
])
const renderAccountRoute = () => {
  return {
    path: "/account",
    name: "Account",
    meta: { layout: "TwoPaneLayout", isNeedAuth: false, gap: 15, mode: "page" },
    components: {
      "left-group": modules[`../components/desktop/menu/Account.vue`],
      "right-group": modules[`../layouts/desktop/Account.vue`]
    },
    children: [
      {
        path: "/account/profile",
        name: "Profile",
        component: modules[`../views/desktop/profile/Profile.vue`]
      },
      {
        path: "/account/accountStatement",
        name: "AccountStatement",
        component: modules[`../views/desktop/accountStatement/AccountStatement.vue`]
      },
      {
        path: "/account/transferredLog",
        name: "TransferredLog",
        component: modules[`../views/desktop/transferredLog/TransferredLog.vue`]
      },
      {
        path: "/account/activityLog",
        name: "ActivityLog",
        component: modules[`../views/desktop/activityLog/ActivityLog.vue`]
      },
      {
        path: "/account/balanceOverview",
        name: "BalanceOverview",
        component: modules[`../views/desktop/balanceOverview/BalanceOverview.vue`]
      },
      {
        path: "/account/myBets",
        name: "MyBets",
        component: modules[`../views/desktop/myBets/MyBets.vue`],
        children: [
          {
            path: "/account/myBets/currentBets",
            name: "CurrentBets",
            component: modules[`../views/desktop/myBets/CurrentBets.vue`]
          },
          {
            path: "/account/myBets/betsHistory",
            name: "BetsHistory",
            component: modules[`../views/desktop/myBets/BetsHistory.vue`]
          },
          {
            path: "/account/myBets/profitLoss",
            name: "ProfitLoss",
            component: modules[`../views/desktop/myBets/ProfitLoss.vue`]
          }
        ]
      }
    ]
  }
}

const renderHomePage = (
  isRacingWebSite: boolean,
  isEnableGameHall: QueryInItInfoResponse["isEnableGameHall"]
): RouteRecordRaw[] => {
  if (isRacingWebSite) {
    return [
      // BAJIHORSE landing page is horse racing page
      {
        name: "Highlight",
        path: "/racing-old",
        redirect: { name: "Home" }
      },
      {
        path: "/racing",
        name: "Home",
        meta: {
          gap: 15,
          layout: "ThreePaneLayout",
          isNeedAuth: false,
          mode: "default"
        },
        components: {
          "left-group": modules[`../components/desktop/menu/leagues/index.vue`],
          "middle-group": modules[`../views/desktop/greyhound/Greyhound.vue`],
          "right-group": modules[`../layouts/desktop/RightGroup.vue`]
        }
      }
    ]
  }
  if (isEnableGameHall) {
    return [
      {
        path: "/gameHall",
        name: "Home",
        meta: {
          gap: "0px",
          mode: "page",
          layout: "SinglePaneLayout",
          isNeedAuth: false
        },
        components: {
          default: modules[`../views/desktop/gamehall/GameHall.vue`]
        }
      },
      {
        path: "/highlight",
        name: "Highlight",
        meta: {
          gap: 15,
          mode: "default",
          layout: "ThreePaneLayout",
          isNeedAuth: false
        },
        components: {
          "left-group": modules[`../components/desktop/menu/leagues/index.vue`],
          "middle-group": modules[`../views/desktop/highlight/Highlight.vue`],
          "right-group": modules[`../layouts/desktop/RightGroup.vue`]
        }
      }
    ]
  }
  return [
    {
      name: "Highlight",
      path: "/highlight-old",
      redirect: { name: "Home" }
    },
    {
      path: "/highlight",
      name: "Home",
      meta: {
        gap: 15,
        mode: "default",
        layout: "ThreePaneLayout",
        isNeedAuth: false
      },
      components: {
        "left-group": modules[`../components/desktop/menu/leagues/index.vue`],
        "middle-group": modules[`../views/desktop/highlight/Highlight.vue`],
        "right-group": modules[`../layouts/desktop/RightGroup.vue`]
      }
    }
  ]
}

export const renderRoutes = (data: QueryInItInfoResponse): RouteRecordRaw[] => {
  const { isRacingWebSite, isEnableGameHall } = data

  return [
    {
      path: "/",
      component: modules[`../layouts/desktop/Default.vue`],
      redirect: { name: "Home" },
      children: [
        {
          path: "/inPlay",
          name: "In-Play",
          meta: { gap: 15, layout: "TwoPaneLayout", mode: "default" },
          components: {
            "left-group": modules[`../views/desktop/inPlay/InPlay.vue`],
            "right-group": modules[`../layouts/desktop/RightGroup.vue`]
          }
        },
        {
          path: "/result",
          name: "In-Play-Result",
          meta: { gap: 15, layout: "TwoPaneLayout", mode: "default" },
          components: {
            "left-group": modules[`../views/desktop/results/Results.vue`],
            "right-group": modules[`../layouts/desktop/RightGroup.vue`]
          }
        },
        {
          path: "/fullMarket/:eventType-:eventId",
          name: "FullMarket",
          meta: { gap: 5, layout: "ThreePaneLayout", mode: "default" },
          components: {
            "left-group": modules[`../components/desktop/menu/leagues/index.vue`],
            "middle-group": modules[`../views/desktop/fullMarket/FullMarket.vue`],
            "right-group": modules[`../layouts/desktop/RightGroup.vue`]
          }
        },
        {
          path: "/multiMarket",
          name: "MultiMarket",
          meta: {
            gap: 15,
            layout: "ThreePaneLayout",
            isNeedAuth: false,
            mode: "default"
          },
          components: {
            "left-group": modules[`../components/desktop/menu/leagues/index.vue`],
            "middle-group": modules[`../views/desktop/multiMarket/MultiMarket.vue`],
            "right-group": modules[`../layouts/desktop/RightGroup.vue`]
          }
        },
        ...renderHomePage(isRacingWebSite, isEnableGameHall),
        renderAccountRoute()
      ]
    },
    {
      path: "/approval",
      component: () => import("../layouts/desktop/Empty.vue"),
      children: [
        {
          path: "/terms",
          name: "Terms",
          component: modules[`../views/desktop/approval/Terms.vue`]
        },
        {
          path: "/resetPassword",
          name: "ResetPassword",
          component: modules[`../views/desktop/approval/ResetPassword.vue`]
        }
      ]
    },
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      component: modules[`../views/desktop/pageError/404.vue`]
    }
  ]
}

export default {}
